import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from './lib/supabase';
import { BarChart3, LogOut, UserPlus, PlusCircle, ListChecks, X, Edit, Moon, Sun, Filter, Users, Check } from 'lucide-react';
import { v4 as uuid } from 'uuid';
import { Bar } from 'react-chartjs-2';
import { toast } from 'react-toastify';

type Employee = {
  user_id: string;
  email: string;
  organization: string;
  first_name: string;
  last_name: string;
  is_delegate?: boolean;
};

type NewLead = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company: string;
  status: string;
  description: string;
};

type Lead = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company: string;
  status: string;
  organization: string;
  description: string;
  converted: boolean;
  created_at: string;
  user_id: string;
  visible_to: string;
};

type Todo = {
  id: string;
  text: string;
  completed: boolean;
};

const ManagementDashboard = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNewUserModalOpen, setIsNewUserModalOpen] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: ''
  });
  const [managerOrganization, setManagerOrganization] = useState('');
  const [employeeCount, setEmployeeCount] = useState(0);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [newLead, setNewLead] = useState<NewLead>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    status: 'new',
    description: ''
  });
  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadCount, setLeadCount] = useState(0);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode ? JSON.parse(savedMode) : false;
  });
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<{
    user_id: string;
    email: string;
    firstName: string;
    lastName: string;
    newPassword?: string;
    isLeadDelegator?: boolean;
  } | null>(null);
  const [newTodo, setNewTodo] = useState('');
  const [todos, setTodos] = useState<Todo[]>([]);
  const [editingTodo, setEditingTodo] = useState<Todo | null>(null);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [isAssignMode, setIsAssignMode] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState<number[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<{
    status: string;
    converted: boolean | null;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    company: string;
    description: string;
  }>({
    status: '',
    converted: null,
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    description: ''
  });
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isDetailEditMode, setIsDetailEditMode] = useState(false);
  const [editingDetailLead, setEditingDetailLead] = useState<Lead | null>(null);
  const [expandedLeads, setExpandedLeads] = useState<number[]>([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [selectedEmployeeStats, setSelectedEmployeeStats] = useState<string | null>(null);
  const [isEmployeeStatsOpen, setIsEmployeeStatsOpen] = useState(false);
  const [showingAssignedLeads, setShowingAssignedLeads] = useState(false);
  const [assignedLeads, setAssignedLeads] = useState<Lead[]>([]);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Function to fetch employees
  const fetchEmployees = async (org: string) => {
    try {
      const { data: employees, error } = await supabase
        .from('user_organization')
        .select('user_id, email, first_name, last_name, organization, is_delegate')
        .eq('organization', org);

      if (error) throw error;
      console.log('Fetched employees:', employees); // Add this line to debug
      setEmployees(employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  // Function to fetch leads
  const fetchLeads = async (org: string) => {
    try {
      // Fetch all leads that belong to this organization, ignoring visible_to
      const { data: fetchedLeads, error } = await supabase
        .from('leads')
        .select('*')
        .eq('organization', org)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      console.log('Fetched leads for organization:', fetchedLeads);
      setLeads(fetchedLeads || []);
      
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  };

  // Function to fetch lead count
  const fetchLeadCount = async (org: string) => {
    try {
      const { count, error } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('organization', org);

      if (error) throw error;
      setLeadCount(count || 0);
    } catch (error) {
      console.error('Error fetching lead count:', error);
    }
  };

  // Fetch the logged-in manager's organization and employee count
  useEffect(() => {
    async function fetchManagerData() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // Get manager's organization
          const { data: managementData, error: orgError } = await supabase
            .from('management_users')
            .select('organization')
            .eq('user_id', user.id)
            .single();

          if (orgError) throw orgError;
          if (managementData) {
            setManagerOrganization(managementData.organization);
            
            // Get count of employees in this organization
            const { count, error: countError } = await supabase
              .from('user_organization')
              .select('*', { count: 'exact', head: true })
              .eq('organization', managementData.organization);

            if (countError) throw countError;
            setEmployeeCount(count || 0);
            await fetchEmployees(managementData.organization);
            await fetchLeads(managementData.organization);
            await fetchLeadCount(managementData.organization);
          }
        }
      } catch (error) {
        console.error('Error fetching manager data:', error);
      }
    }
    fetchManagerData();
  }, []);

  // Modify subscription to update employee list as well
  useEffect(() => {
    if (!managerOrganization) return;

    const subscription = supabase
      .channel('user_organization_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_organization',
          filter: `organization=eq.${managerOrganization}`
        },
        () => {
          fetchEmployees(managerOrganization);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [managerOrganization]);

  // Add subscription for leads table
  useEffect(() => {
    if (!managerOrganization) return;

    const subscription = supabase
      .channel('leads_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leads',
          filter: `organization=eq.${managerOrganization}`
        },
        () => {
          fetchLeads(managerOrganization);
          fetchLeadCount(managerOrganization);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [managerOrganization]);

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Clear any local storage items we've set
      localStorage.removeItem('theme');
      localStorage.removeItem('darkMode');
      
      // Force reload the page to clear any persisted state
      window.location.reload();
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Error signing out: ' + (error as Error).message);
    }
  };

  const handleNewUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { data: userData, error: signUpError } = await supabase.auth.signUp({ 
        email: newUserForm.email, 
        password: newUserForm.password,
        options: {
          data: {
            is_management: false,
            first_name: newUserForm.firstName,
            last_name: newUserForm.lastName
          }
        }
      });
      
      if (signUpError) throw signUpError;
      
      if (!userData?.user?.id) {
        throw new Error('User creation failed - no user ID returned');
      }

      const { error: mappingError } = await supabase
        .from('user_organization')
        .insert({
          user_id: userData.user.id,
          organization: managerOrganization,
          email: newUserForm.email,
          first_name: newUserForm.firstName,
          last_name: newUserForm.lastName
        });

      if (mappingError) throw mappingError;
      
      await fetchEmployees(managerOrganization);
      const { count } = await supabase
        .from('user_organization')
        .select('*', { count: 'exact', head: true })
        .eq('organization', managerOrganization);
      setEmployeeCount(count || 0);
      
      alert("User created successfully! They will need to verify their email.");
      setIsNewUserModalOpen(false);
      setNewUserForm({ email: '', password: '', firstName: '', lastName: '' });
      
    } catch (error) {
      console.error('Error creating user:', error);
      alert("Error creating user: " + (error as Error).message);
    }
  };

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error('No authenticated user found');

      const { error } = await supabase
        .from('leads')
        .insert([{ 
          ...newLead, 
          organization: managerOrganization,
          user_id: user.id,
          converted: false
        }]);

      if (error) throw error;
      
      // Refresh the leads list and count after creating a new lead
      await fetchLeads(managerOrganization);
      await fetchLeadCount(managerOrganization);
      
      alert('Lead created successfully!');
      setIsModalOpen(false);
      setNewLead({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        company: '',
        status: 'new',
        description: ''
      });
    } catch (error) {
      console.error('Error creating lead:', error);
      alert('Error creating lead: ' + (error as Error).message);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingLead) return;

    try {
      // Update in Supabase
      const { error } = await supabase
        .from('leads')
        .update({
          first_name: editingLead.first_name,
          last_name: editingLead.last_name,
          email: editingLead.email,
          phone: editingLead.phone,
          company: editingLead.company,
          status: editingLead.status,
          description: editingLead.description
        })
        .eq('id', editingLead.id);

      if (error) throw error;

      // Update UI
      setLeads(currentLeads => 
        currentLeads.map(lead => 
          lead.id === editingLead.id ? editingLead : lead
        )
      );

      setIsEditModalOpen(false);
      setEditingLead(null);

    } catch (error) {
      console.error('Error updating lead:', error);
      alert('Error updating lead: ' + (error as Error).message);
    }
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditingLead(prev => prev ? { ...prev, [name]: value } : null);
  };

  const handleEditUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    try {
      // Update user in the database
      const { error } = await supabase
        .from('user_organization')
        .update({
          first_name: editingUser.firstName,
          last_name: editingUser.lastName,
          email: editingUser.email,
          is_delegate: editingUser.isLeadDelegator || false
        })
        .eq('user_id', editingUser.user_id);

      if (error) throw error;

      // If a new password was provided, update it
      if (editingUser.newPassword) {
        // Update password logic here
        // This depends on your authentication setup
      }

      // Refresh employees list with the organization parameter
      await fetchEmployees(managerOrganization);
      
      setIsEditUserModalOpen(false);
      setEditingUser(null);
      alert('User updated successfully');
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user: ' + (error as Error).message);
    }
  }

  const handleEditUserInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditingUser(prev => prev ? { ...prev, [name]: value } : null);
  };

  const fetchTodos = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from('todos')
      .select('*')
      .eq('user_id', user.id);
    
    console.log('Todos from DB:', data);
    if (data) setTodos(data);
  };

  useEffect(() => {
    fetchTodos();
  }, []);

  const handleAddTodo = async (e) => {
    e.preventDefault();
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('todos')
      .insert([{
        text: newTodo,
        user_id: user.id,
        organization: managerOrganization,
        completed: false,
        due_date: new Date().toISOString()
      }]);

    console.log('Add response:', { data, error });
    
    if (!error) {
      setNewTodo('');
      const { data: newData } = await supabase
        .from('todos')
        .select('*')
        .eq('user_id', user.id);
      if (newData) setTodos(newData);
    }
  };

  const handleToggleTodo = async (id) => {
    // Update UI immediately
    setTodos(currentTodos => 
      currentTodos.map(todo => 
        todo.id === id ? { ...todo, completed: !todo.completed } : todo
      )
    );
    
    // Then update database
    const todo = todos.find(t => t.id === id);
    const { error } = await supabase
      .from('todos')
      .update({ completed: !todo.completed })
      .eq('id', id);

    if (error) {
      console.error('Error toggling todo:', error);
      // Revert UI if there was an error
      const { data } = await supabase
        .from('todos')
        .select('*')
        .eq('user_id', user.id);
      if (data) setTodos(data);
    }
  };

  const handleDeleteTodo = async (id) => {
    // Update UI immediately
    setTodos(currentTodos => currentTodos.filter(todo => todo.id !== id));
    
    // Then update database
    const { error } = await supabase
      .from('todos')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting todo:', error);
      // Revert UI if there was an error
      const { data } = await supabase
        .from('todos')
        .select('*')
        .eq('user_id', user.id);
      if (data) setTodos(data);
    }
  };

  const handleUpdateTodo = async (e) => {
    e.preventDefault();
    if (!editingTodo) return;

    // Update UI immediately
    setTodos(currentTodos => 
      currentTodos.map(todo => 
        todo.id === editingTodo.id ? { ...todo, text: editingTodo.text } : todo
      )
    );
    setEditingTodo(null);
    
    // Then update database
    const { error } = await supabase
      .from('todos')
      .update({ text: editingTodo.text })
      .eq('id', editingTodo.id);

    if (error) {
      console.error('Error updating todo:', error);
      // Revert UI if there was an error
      const { data } = await supabase
        .from('todos')
        .select('*')
        .eq('user_id', user.id);
      if (data) setTodos(data);
    }
  };

  // Add handlers for selection
  const handleLeadSelect = (leadId: number) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleUserSelect = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? [] // If clicking selected user, clear selection
        : [userId] // If clicking new user, make it the only selection
    );
  };

  // Add this function to handle lead assignment
  const handleAssignLeads = async () => {
    if (!selectedUsers[0] || selectedLeads.length === 0) return;
    
    try {
      const userId = selectedUsers[0];
      
      // Update all selected leads at once
      const { error } = await supabase
        .from('leads')
        .update({ visible_to: userId })
        .in('id', selectedLeads);

      if (error) throw error;

      // Reset state after successful assignment
      setSelectedLeads([]);
      setSelectedUsers([]);
      setIsAssignMode(false);
      
      // Refresh leads data
      if (managerOrganization) {
        await fetchLeads(managerOrganization);
      }
      
      alert('Leads assigned successfully!');
      
    } catch (error) {
      console.error('Error assigning leads:', error);
      alert('Error assigning leads: ' + (error as Error).message);
    }
  };

  // Modify the useEffect that handles filtering
  useEffect(() => {
    const filtered = leads.filter(lead => {
      // Name search filter
      const fullName = `${lead.first_name} ${lead.last_name}`.toLowerCase();
      const matchesSearch = fullName.includes(searchQuery.toLowerCase());
      
      // Field filters
      const matchesStatus = !activeFilters.status || lead.status === activeFilters.status;
      const matchesConverted = activeFilters.converted === null || lead.converted === activeFilters.converted;
      const matchesFirstName = !activeFilters.first_name || lead.first_name.toLowerCase().includes(activeFilters.first_name.toLowerCase());
      const matchesLastName = !activeFilters.last_name || lead.last_name.toLowerCase().includes(activeFilters.last_name.toLowerCase());
      const matchesEmail = !activeFilters.email || lead.email.toLowerCase().includes(activeFilters.email.toLowerCase());
      const matchesPhone = !activeFilters.phone || lead.phone.includes(activeFilters.phone);
      const matchesCompany = !activeFilters.company || lead.company.toLowerCase().includes(activeFilters.company.toLowerCase());
      const matchesDescription = !activeFilters.description || lead.description.toLowerCase().includes(activeFilters.description.toLowerCase());

      return matchesSearch && 
             matchesStatus && 
             matchesConverted && 
             matchesFirstName && 
             matchesLastName && 
             matchesEmail && 
             matchesPhone && 
             matchesCompany && 
             matchesDescription;
    });
    setFilteredLeads(filtered);
  }, [leads, searchQuery, activeFilters]);

  const handleDetailSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDetailLead) return;

    try {
      const { error } = await supabase
        .from('leads')
        .update({
          first_name: editingDetailLead.first_name,
          last_name: editingDetailLead.last_name,
          email: editingDetailLead.email,
          phone: editingDetailLead.phone,
          company: editingDetailLead.company,
          status: editingDetailLead.status,
          description: editingDetailLead.description,
          converted: editingDetailLead.converted
        })
        .eq('id', editingDetailLead.id);

      if (error) throw error;

      setLeads(leads.map(lead => 
        lead.id === editingDetailLead.id ? editingDetailLead : lead
      ));
      setSelectedLead(editingDetailLead);
      setIsDetailEditMode(false);
      setEditingDetailLead(null);

    } catch (error) {
      console.error('Error updating lead:', error);
      alert('Error updating lead: ' + (error as Error).message);
    }
  };

  const toggleLeadExpansion = (leadId: number) => {
    setExpandedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  console.log('Before render - todos:', todos);

  const filteredEmployees = useMemo(() => {
    return employees.filter(employee => {
      const fullName = `${employee.first_name || ''} ${employee.last_name || ''}`.toLowerCase();
      return fullName.includes(userSearchQuery.toLowerCase());
    });
  }, [employees, userSearchQuery]);

  // Update the helper function to get actual employee stats
  const getEmployeeLeadStats = (userId: string) => {
    // Get all leads assigned to this employee (using visible_to instead of user_id)
    const employeeLeads = leads.filter(lead => lead.visible_to === userId);
    
    // Get monthly data
    const getMonthlyLeads = () => {
      const monthlyData = {};
      employeeLeads.forEach(lead => {
        const date = new Date(lead.created_at);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = {
            total: 0,
            converted: 0
          };
        }
        monthlyData[monthKey].total++;
        if (lead.converted) {
          monthlyData[monthKey].converted++;
        }
      });

      // Generate month keys for the current month and the six months prior
      const monthsToDisplay: string[] = [];
      const currentDate = new Date();
      // Normalize to the first day of the current month
      currentDate.setDate(1);

      for (let i = 6; i >= 0; i--) {
        const dt = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        const key = `${dt.getFullYear()}-${(dt.getMonth() + 1).toString().padStart(2, '0')}`;
        monthsToDisplay.push(key);
      }

      // Map the generated months to conversion rate data. If no data exists for a month, use zero.
      return monthsToDisplay.map((month) => {
        const stat = monthlyData[month] || { total: 0, converted: 0 };
        const rate = stat.total > 0 ? ((stat.converted / stat.total) * 100).toFixed(0) : "0";
        return [month, { total: stat.total, converted: stat.converted, rate: Number(rate) }];
      });
    };

    // Get weekly data
    const getWeeklyLeads = () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Normalize today to start of day
      
      // Start from the Monday of the current week
      const currentMonday = new Date(today);
      currentMonday.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
      
      // Create array of the last 12 weeks
      const weeks = [];
      for (let i = 11; i >= 0; i--) {
        const weekStart = new Date(currentMonday);
        weekStart.setDate(currentMonday.getDate() - (7 * i));
        weeks.push(weekStart.toISOString().split('T')[0]);
      }
      
      // Initialize data for all weeks with 0
      const weeklyData = Object.fromEntries(weeks.map(week => [week, 0]));
      
      // Fill in actual lead counts
      employeeLeads.forEach(lead => {
        const leadDate = new Date(lead.created_at);
        leadDate.setHours(0, 0, 0, 0); // Normalize lead date to start of day
        
        // Find the Monday of the lead's week
        const leadMonday = new Date(leadDate);
        leadMonday.setDate(leadDate.getDate() - leadDate.getDay() + (leadDate.getDay() === 0 ? -6 : 1));
        const weekKey = leadMonday.toISOString().split('T')[0];
        
        if (weekKey in weeklyData) {
          weeklyData[weekKey]++;
        }
      });

      // Convert to array format and sort
      return Object.entries(weeklyData)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([week, count]) => ({ week, count }));
    };

    // Calculate current stats
    const convertedLeads = employeeLeads.filter(lead => lead.converted);
    const activeLeads = employeeLeads.filter(lead => !lead.converted && lead.status !== 'closed');
    const conversionRate = employeeLeads.length > 0 
      ? ((convertedLeads.length / employeeLeads.length) * 100).toFixed(1) 
      : '0';

    return {
      totalLeads: employeeLeads.length,
      convertedLeads: convertedLeads.length,
      activeLeads: activeLeads.length,
      conversionRate,
      monthlyData: getMonthlyLeads(),
      weeklyData: getWeeklyLeads()
    };
  };

  // Add function to fetch assigned leads
  const fetchAssignedLeads = async (userId: string) => {
    try {
      const { data: leads, error } = await supabase
        .from('leads')
        .select('*')
        .eq('visible_to', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAssignedLeads(leads || []);
    } catch (error) {
      console.error('Error fetching assigned leads:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="p-4 flex justify-between items-center bg-white dark:bg-gray-800 shadow">
        <h1 className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
          {managerOrganization ? `${managerOrganization} Management Dashboard` : 'Management Dashboard'}
        </h1>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            aria-label="Toggle dark mode"
          >
            {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          <button
            onClick={handleSignOut}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            Sign Out
          </button>
        </div>
      </div>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-2 mb-8">
          {[
            { 
              label: 'Total Leads', 
              value: leadCount.toString(), 
              color: 'bg-blue-500'
            },
            { 
              label: 'Total Employees', 
              value: employeeCount.toString(), 
              color: 'bg-green-500'
            }
          ].map((stat) => (
            <div 
              key={stat.label} 
              className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <BarChart3 className={`h-6 w-6 ${stat.color} text-white rounded p-1`} />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        {stat.label}
                      </dt>
                      <dd className="text-lg font-semibold text-gray-900 dark:text-white">
                        {stat.value}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Management Actions */}
        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg p-6">
          <h2 className="text-xl font-semibold text-indigo-600 dark:text-indigo-400 mb-6">
            Management Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => setIsNewUserModalOpen(true)}
              className="flex items-center justify-center px-4 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <UserPlus className="h-5 w-5 mr-2" />
              Set up new user
            </button>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <PlusCircle className="h-5 w-5 mr-2" />
              Create Lead
            </button>
            <button
              onClick={() => setIsManageModalOpen(true)}
              className="flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <ListChecks className="h-5 w-5 mr-2" />
              Manage Leads
            </button>
          </div>
        </div>

        {/* Replace the existing lists section with this new layout */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mt-8">
          {/* Todo List Panel - Full width on mobile, 1/4 width on desktop */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg sticky top-4">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-indigo-600 dark:text-indigo-400">
                  To-Do List
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                  Track your tasks and priorities
                </p>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700">
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {todos.map(todo => (
                    <li key={todo.id} className="px-4 py-4">
                      {editingTodo?.id === todo.id ? (
                        <form onSubmit={handleUpdateTodo} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={editingTodo.text}
                            onChange={e => setEditingTodo({ ...editingTodo, text: e.target.value })}
                            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                          />
                          <button
                            type="submit"
                            className="text-sm text-green-600 hover:text-green-700 dark:text-green-400"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingTodo(null)}
                            className="text-sm text-gray-600 hover:text-gray-700 dark:text-gray-400"
                          >
                            Cancel
                          </button>
                        </form>
                      ) : (
                        <div className="flex items-center justify-between group">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={todo.completed || false}
                              onChange={() => handleToggleTodo(todo.id)}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                            />
                            <p className={`ml-3 text-sm ${
                              todo.completed 
                                ? 'text-gray-400 line-through' 
                                : 'text-gray-900 dark:text-gray-400'
                            }`}>
                              {todo.text}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => setEditingTodo(todo)}
                              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteTodo(todo.id)}
                              className="text-sm text-red-600 hover:text-red-700 dark:text-red-400"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                {/* Add Todo Input */}
                <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700">
                  <form onSubmit={handleAddTodo}>
                    <div className="flex items-center">
                      <input
                        type="text"
                        value={newTodo}
                        onChange={(e) => setNewTodo(e.target.value)}
                        placeholder="Add new todo..."
                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                      />
                      <button
                        type="submit"
                        className="ml-2 inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Add
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* Main content - Full width on mobile, 3/4 width on desktop */}
          <div className="lg:col-span-3">
            {/* Employee List */}
            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-indigo-600 dark:text-indigo-400">
                  Organization Employees
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                  List of all employees in your organization
                </p>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700">
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {employees.map((employee) => (
                    <li 
                      key={employee.user_id} 
                      className="px-4 py-4 sm:px-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                      onClick={() => {
                        setSelectedEmployeeStats(employee.user_id);
                        setIsEmployeeStatsOpen(true);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                          <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400 truncate">
                            {employee.email}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {employee.first_name} {employee.last_name}
                          </p>
                        </div>
                        <div className="ml-2 flex items-center space-x-2">
                          <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100">
                            Active
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Stop the click from bubbling up to the parent
                              setEditingUser({
                                user_id: employee.user_id,
                                email: employee.email,
                                firstName: employee.first_name,
                                lastName: employee.last_name,
                                isLeadDelegator: employee.is_delegate || false // Set checkbox based on is_delegate value
                              });
                              setIsEditUserModalOpen(true);
                            }}
                            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Leads List */}
            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg mt-8">
              <div className="px-4 py-5 sm:px-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-indigo-600 dark:text-indigo-400">
                      Organization Leads
                    </h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                      List of all leads in your organization
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      placeholder="Search by name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    <div className="relative">
                      <button
                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                        className="px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center space-x-2"
                      >
                        <Filter className="h-4 w-4" />
                        <span>Filter</span>
                        {(activeFilters.status || activeFilters.converted !== null) && (
                          <span className="flex h-2 w-2 relative">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700">
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredLeads.map((lead) => (
                    <li 
                      key={lead.id} 
                      className="px-4 py-4 sm:px-6 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      onClick={() => setSelectedLead(lead)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                            {lead.first_name} {lead.last_name}
                          </p>
                          <div className="mt-1 text-sm text-gray-500 dark:text-gray-400 space-x-4">
                            <span>{lead.email}</span>
                            <span>•</span>
                            <span>{lead.phone}</span>
                            <span>•</span>
                            <span>{lead.description}</span>
                          </div>
                        </div>
                        <div className="ml-2 flex items-center space-x-2">
                          <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100">
                            {lead.status}
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent the li onClick from firing
                              setSelectedLead(lead);
                              setEditingDetailLead({...lead});
                              setIsDetailEditMode(true);
                            }}
                            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Create Lead Modal - UI Only */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
              <div className="flex justify-between items-center p-6 border-b">
                <h2 className="text-xl font-semibold text-indigo-600 dark:text-indigo-400">Create New Lead</h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <form onSubmit={handleLeadSubmit} className="p-6">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={newLead.first_name}
                      onChange={(e) => setNewLead(prev => ({ ...prev, first_name: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 dark:bg-gray-700 dark:border-gray-600 rounded-md shadow-sm p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={newLead.last_name}
                      onChange={(e) => setNewLead(prev => ({ ...prev, last_name: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 dark:bg-gray-700 dark:border-gray-600 rounded-md shadow-sm p-2"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newLead.email}
                    onChange={(e) => setNewLead(prev => ({ ...prev, email: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 dark:bg-gray-700 dark:border-gray-600 rounded-md shadow-sm p-2"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={newLead.phone}
                    onChange={(e) => setNewLead(prev => ({ ...prev, phone: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 dark:bg-gray-700 dark:border-gray-600 rounded-md shadow-sm p-2"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Company
                  </label>
                  <input
                    type="text"
                    value={newLead.company}
                    onChange={(e) => setNewLead(prev => ({ ...prev, company: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 dark:bg-gray-700 dark:border-gray-600 rounded-md shadow-sm p-2"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Status
                  </label>
                  <select
                    value={newLead.status}
                    onChange={(e) => setNewLead(prev => ({ ...prev, status: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 rounded-md shadow-sm p-2"
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="qualified">Qualified</option>
                    <option value="proposal">Proposal</option>
                    <option value="negotiation">Negotiation</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Description
                  </label>
                  <textarea
                    value={newLead.description}
                    onChange={(e) => setNewLead(prev => ({ ...prev, description: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 dark:bg-gray-700 dark:border-gray-600 rounded-md shadow-sm p-2"
                    rows={3}
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* New User Modal - UI Only */}
        {isNewUserModalOpen && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
              <div className="flex justify-between items-center p-6 border-b">
                <h2 className="text-xl font-semibold text-indigo-600 dark:text-indigo-400">Set Up New User</h2>
                <button
                  onClick={() => setIsNewUserModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <form onSubmit={handleNewUserSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={newUserForm.firstName}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, firstName: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={newUserForm.lastName}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, lastName: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newUserForm.email}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, email: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Password
                  </label>
                  <input
                    type="password"
                    value={newUserForm.password}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, password: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                  />
                </div>
                <div className="flex justify-end space-x-2 mt-6">
                  <button
                    type="button"
                    onClick={() => setIsNewUserModalOpen(false)}
                    className="px-4 py-2 text-sm text-gray-700 bg-white border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    Create User
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Lead Modal */}
        {isEditModalOpen && editingLead && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
              <div className="flex justify-between items-center p-6 border-b">
                <h2 className="text-xl font-semibold text-indigo-600 dark:text-indigo-400">Edit Lead</h2>
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <form onSubmit={handleEditSubmit} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      value={editingLead.first_name}
                      onChange={handleEditInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      value={editingLead.last_name}
                      onChange={handleEditInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={editingLead.email}
                      onChange={handleEditInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={editingLead.phone}
                      onChange={handleEditInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={editingLead.company}
                      onChange={handleEditInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Status
                    </label>
                    <select
                      name="status"
                      value={editingLead.status}
                      onChange={handleEditInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="qualified">Qualified</option>
                      <option value="proposal">Proposal</option>
                      <option value="negotiation">Negotiation</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={editingLead.description}
                      onChange={handleEditInputChange}
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                    />
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setEditingLead(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {isEditUserModalOpen && editingUser && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
              <div className="flex justify-between items-center p-6 border-b">
                <h2 className="text-xl font-semibold text-indigo-600 dark:text-indigo-400">Edit User</h2>
                <button
                  onClick={() => setIsEditUserModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <form onSubmit={handleEditUserSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={editingUser.firstName}
                    onChange={(e) => setEditingUser(prev => prev ? { ...prev, firstName: e.target.value } : null)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={editingUser.lastName}
                    onChange={(e) => setEditingUser(prev => prev ? { ...prev, lastName: e.target.value } : null)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email
                  </label>
                  <input
                    type="email"
                    value={editingUser.email}
                    onChange={(e) => setEditingUser(prev => prev ? { ...prev, email: e.target.value } : null)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    New Password (leave blank to keep unchanged)
                  </label>
                  <input
                    type="password"
                    value={editingUser.newPassword || ''}
                    onChange={(e) => setEditingUser(prev => prev ? { ...prev, newPassword: e.target.value } : null)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editingUser.isLeadDelegator || false}
                      onChange={(e) => setEditingUser(prev => prev ? { ...prev, isLeadDelegator: e.target.checked } : null)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Set as Lead Delegator</span>
                  </label>
                </div>
                <div className="flex justify-end space-x-2 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditUserModalOpen(false);
                      setEditingUser(null);
                    }}
                    className="px-4 py-2 text-sm text-gray-700 bg-white border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Update the Manage Modal */}
        {isManageModalOpen && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
              <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-4">
                  <h2 className="text-xl font-semibold text-indigo-600 dark:text-indigo-400">Manage Organization</h2>
                  <button
                    onClick={() => {
                      setIsAssignMode(!isAssignMode);
                      // Clear selections when toggling mode
                      if (!isAssignMode) {
                        setSelectedLeads([]);
                        setSelectedUsers([]);
                      }
                    }}
                    className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                      isAssignMode 
                        ? 'bg-red-600 hover:bg-red-700' 
                        : 'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                  >
                    {isAssignMode ? 'Cancel Assignment' : 'Assign Leads'}
                  </button>
                  {/* Update the Assign button */}
                  {isAssignMode && selectedLeads.length > 0 && selectedUsers.length > 0 && (
                    <button
                      onClick={handleAssignLeads}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      Assign
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setIsManageModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <div className="p-6 grid grid-cols-2 gap-6">
                  {/* Leads List */}
                  <div>
                    <h3 className="text-lg font-medium text-indigo-600 dark:text-indigo-400 mb-4">Organization Leads</h3>
                    <div className="flex items-center space-x-2 mb-4">
                      <input
                        type="text"
                        placeholder="Search by name..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                      <div className="relative">
                        <button
                          onClick={() => setIsFilterOpen(!isFilterOpen)}
                          className="px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center space-x-2"
                        >
                          <Filter className="h-4 w-4" />
                          <span>Filter</span>
                          {(activeFilters.status || activeFilters.converted !== null) && (
                            <span className="flex h-2 w-2 relative">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-700 shadow rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                          <tr>
                            {isAssignMode && (
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Select
                              </th>
                            )}
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Company</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"></th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
                          {filteredLeads.map((lead) => (
                            <React.Fragment key={lead.id}>
                              <tr className="hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer" 
                                  onClick={() => toggleLeadExpansion(lead.id)}>
                                {isAssignMode && (
                                  <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                    <input
                                      type="checkbox"
                                      checked={selectedLeads.includes(lead.id)}
                                      onChange={() => handleLeadSelect(lead.id)}
                                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                                    />
                                  </td>
                                )}
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {lead.first_name} {lead.last_name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                                  {lead.company}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100">
                                    {lead.status}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleLeadExpansion(lead.id);
                                    }}
                                    className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                                  >
                                    {expandedLeads.includes(lead.id) ? 'Hide Details' : 'Show Details'}
                                  </button>
                                </td>
                              </tr>
                              {expandedLeads.includes(lead.id) && (
                                <tr className="bg-gray-50 dark:bg-gray-600">
                                  <td colSpan={isAssignMode ? 5 : 4} className="px-6 py-4">
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                      <div>
                                        <p className="font-medium text-gray-700 dark:text-gray-300">Contact Information</p>
                                        <div className="mt-2 space-y-1">
                                          <p className="text-gray-600 dark:text-gray-400">
                                            <span className="font-medium">Email:</span> {lead.email}
                                          </p>
                                          <p className="text-gray-600 dark:text-gray-400">
                                            <span className="font-medium">Phone:</span> {lead.phone}
                                          </p>
                                          <p className="text-gray-600 dark:text-gray-400">
                                            <span className="font-medium">Company:</span> {lead.company}
                                          </p>
                                        </div>
                                      </div>
                                      <div>
                                        <p className="font-medium text-gray-700 dark:text-gray-300">Lead Details</p>
                                        <div className="mt-2 space-y-1">
                                          <p className="text-gray-600 dark:text-gray-400">
                                            <span className="font-medium">Status:</span> {lead.status}
                                          </p>
                                          <p className="text-gray-600 dark:text-gray-400">
                                            <span className="font-medium">Converted:</span> {lead.converted ? 'Yes' : 'No'}
                                          </p>
                                          <p className="text-gray-600 dark:text-gray-400">
                                            <span className="font-medium">Description:</span>
                                          </p>
                                          <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                                            {lead.description}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Users List */}
                  <div>
                    <h3 className="text-lg font-medium text-indigo-600 dark:text-indigo-400 mb-4">Organization Users</h3>
                    <div className="flex items-center space-x-2 mb-4">
                      <input
                        type="text"
                        placeholder="Search by name..."
                        value={userSearchQuery}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                    <div className="bg-white dark:bg-gray-700 shadow rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                          <tr>
                            {isAssignMode && (
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Select
                              </th>
                            )}
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Name
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Email
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-900 dark:divide-gray-700">
                          {filteredEmployees.map((employee) => (
                            <tr key={employee.user_id}>
                              {isAssignMode && (
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <input
                                    type="checkbox"
                                    checked={selectedUsers.includes(employee.user_id)}
                                    onChange={() => handleUserSelect(employee.user_id)}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                                  />
                                </td>
                              )}
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                {`${employee.first_name || ''} ${employee.last_name || ''}`}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {employee.email}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100">
                                  Active
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filter Modal - Update to position it relative to the Manage Modal */}
        {isFilterOpen && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg">
              <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-indigo-600 dark:text-indigo-400">
                  Filter Leads
                </h3>
                <button
                  onClick={() => setIsFilterOpen(false)}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </label>
                    <select
                      value={activeFilters.status}
                      onChange={(e) => setActiveFilters(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="">All</option>
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="qualified">Qualified</option>
                      <option value="lost">Lost</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Conversion Status
                    </label>
                    <select
                      value={activeFilters.converted === null ? '' : activeFilters.converted.toString()}
                      onChange={(e) => {
                        const value = e.target.value;
                        setActiveFilters(prev => ({
                          ...prev,
                          converted: value === '' ? null : value === 'true'
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="">All</option>
                      <option value="true">Converted</option>
                      <option value="false">Not Converted</option>
                    </select>
                  </div>

                  {[
                    { key: 'first_name', label: 'First Name' },
                    { key: 'last_name', label: 'Last Name' },
                    { key: 'email', label: 'Email' },
                    { key: 'phone', label: 'Phone' },
                    { key: 'company', label: 'Company' },
                    { key: 'description', label: 'Description' }
                  ].map(field => (
                    <div key={field.key}>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {field.label}
                      </label>
                      <input
                        type="text"
                        value={activeFilters[field.key as keyof typeof activeFilters]}
                        onChange={(e) => setActiveFilters(prev => ({ ...prev, [field.key]: e.target.value }))}
                        placeholder={`Filter by ${field.label.toLowerCase()}...`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-2 p-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => {
                    setActiveFilters({
                      status: '',
                      converted: null,
                      first_name: '',
                      last_name: '',
                      email: '',
                      phone: '',
                      company: '',
                      description: ''
                    });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-gray-500 dark:hover:text-gray-400"
                >
                  Clear Filters
                </button>
                <button
                  onClick={() => setIsFilterOpen(false)}
                  className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Detail View Modal */}
        {selectedLead && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg">
              <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-indigo-600 dark:text-indigo-400">Lead Details</h2>
                <button
                  onClick={() => setSelectedLead(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {isDetailEditMode ? (
                  // Edit Mode Form
                  <form onSubmit={handleDetailSave}>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          First Name
                        </label>
                        <input
                          type="text"
                          value={editingDetailLead?.first_name || ''}
                          onChange={(e) => setEditingDetailLead(prev => prev ? { ...prev, first_name: e.target.value } : null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Last Name
                        </label>
                        <input
                          type="text"
                          value={editingDetailLead?.last_name || ''}
                          onChange={(e) => setEditingDetailLead(prev => prev ? { ...prev, last_name: e.target.value } : null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={editingDetailLead?.email || ''}
                        onChange={(e) => setEditingDetailLead(prev => prev ? { ...prev, email: e.target.value } : null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={editingDetailLead?.phone || ''}
                        onChange={(e) => setEditingDetailLead(prev => prev ? { ...prev, phone: e.target.value } : null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Company
                      </label>
                      <input
                        type="text"
                        value={editingDetailLead?.company || ''}
                        onChange={(e) => setEditingDetailLead(prev => prev ? { ...prev, company: e.target.value } : null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Status
                      </label>
                      <select
                        value={editingDetailLead?.status || ''}
                        onChange={(e) => setEditingDetailLead(prev => prev ? { ...prev, status: e.target.value } : null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="qualified">Qualified</option>
                        <option value="lost">Lost</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description
                      </label>
                      <textarea
                        value={editingDetailLead?.description || ''}
                        onChange={(e) => setEditingDetailLead(prev => prev ? { ...prev, description: e.target.value } : null)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editingDetailLead?.converted || false}
                          onChange={(e) => setEditingDetailLead(prev => prev ? { ...prev, converted: e.target.checked } : null)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Converted</span>
                      </label>
                    </div>
                  </form>
                ) : (
                  // View Mode
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">First Name</p>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedLead.first_name}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Name</p>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedLead.last_name}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</p>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedLead.email}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Phone</p>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedLead.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Company</p>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedLead.company}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</p>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedLead.status}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Description</p>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedLead.description}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Converted</p>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">
                        {selectedLead.converted ? 'Yes' : 'No'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-2 p-6 border-t border-gray-200 dark:border-gray-700">
                {isDetailEditMode ? (
                  <>
                    <button
                      type="button"
                      onClick={handleDetailSave}
                      className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      Save Changes
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsDetailEditMode(false);
                        setEditingDetailLead(null);
                      }}
                      className="px-4 py-2 text-sm text-gray-700 bg-white border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent the li onClick from firing
                        setSelectedLead(selectedLead);
                        setEditingDetailLead({...selectedLead});
                        setIsDetailEditMode(true);
                      }}
                      className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      Edit Lead
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedLead(null);
                        setIsDetailEditMode(false);
                        setEditingDetailLead(null);
                      }}
                      className="px-4 py-2 text-sm text-gray-700 bg-white border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      Close
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {isEmployeeStatsOpen && selectedEmployeeStats && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full">
              <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-indigo-600 dark:text-indigo-400">Employee Performance</h2>
                <button
                  onClick={() => setIsEmployeeStatsOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="p-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
                  {(() => {
                    const stats = getEmployeeLeadStats(selectedEmployeeStats);
                    return [
                      { 
                        label: 'Total Leads', 
                        value: stats.totalLeads,
                        icon: Users,
                        color: 'bg-blue-500',
                        onClick: async () => {
                          setShowingAssignedLeads(true);
                          await fetchAssignedLeads(selectedEmployeeStats);
                        }
                      },
                      { 
                        label: 'Converted Leads', 
                        value: stats.convertedLeads,
                        icon: Check,
                        color: 'bg-green-500'
                      },
                      { 
                        label: 'Conversion Rate', 
                        value: `${stats.conversionRate}%`,
                        icon: BarChart3,
                        color: 'bg-purple-500'
                      },
                      { 
                        label: 'Active Leads', 
                        value: stats.activeLeads,
                        icon: UserPlus,
                        color: 'bg-yellow-500'
                      }
                    ].map((stat) => (
                      <div 
                        key={stat.label} 
                        className={`bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg ${stat.onClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700' : ''}`}
                        onClick={stat.onClick}
                      >
                        <div className="p-5">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <stat.icon className={`h-6 w-6 ${stat.color} text-white rounded p-1`} />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                              <dl>
                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                  {stat.label}
                                </dt>
                                <dd className="text-lg font-semibold text-gray-900 dark:text-white">
                                  {stat.value}
                                </dd>
                              </dl>
                            </div>
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>

                {/* Assigned Leads List or Charts */}
                {showingAssignedLeads ? (
                  <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium text-indigo-600 dark:text-indigo-400">Assigned Leads</h3>
                      <button
                        onClick={() => setShowingAssignedLeads(false)}
                        className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                      >
                        Back to Analytics
                      </button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Company</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Converted</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {assignedLeads.map((lead) => (
                            <tr key={lead.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                {lead.first_name} {lead.last_name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {lead.email}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {lead.company}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100">
                                  {lead.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {lead.converted ? 'Yes' : 'No'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                      <h3 className="text-lg font-medium mb-4 text-indigo-600 dark:text-indigo-400">Weekly New Leads</h3>
                      <Bar
                        data={{
                          labels: getEmployeeLeadStats(selectedEmployeeStats).weeklyData.map(d => {
                            const date = new Date(d.week);
                            return `Week of ${date.toLocaleDateString('default', { month: 'short', day: 'numeric' })}`;
                          }),
                          datasets: [{
                            label: 'New Leads',
                            data: getEmployeeLeadStats(selectedEmployeeStats).weeklyData.map(d => d.count),
                            backgroundColor: 'rgba(34, 197, 94, 0.5)',
                            borderColor: 'rgb(34, 197, 94)',
                            borderWidth: 1
                          }]
                        }}
                        options={{
                          responsive: true,
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: { stepSize: 1 }
                            }
                          }
                        }}
                      />
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                      <h3 className="text-lg font-medium mb-4 text-indigo-600 dark:text-indigo-400">Monthly Conversion Rate</h3>
                      <Bar
                        data={{
                          labels: getEmployeeLeadStats(selectedEmployeeStats).monthlyData.map(([month]) => {
                            const [year, monthNum] = month.split('-');
                            return `${new Date(parseInt(year), parseInt(monthNum) - 1).toLocaleString('default', { month: 'short' })} ${year}`;
                          }),
                          datasets: [
                            {
                              label: 'Conversion Rate (%)',
                              data: getEmployeeLeadStats(selectedEmployeeStats).monthlyData.map(([_, data]) => data.rate),
                              backgroundColor: 'rgba(147, 51, 234, 0.5)',
                              borderColor: 'rgb(147, 51, 234)',
                              borderWidth: 1
                            }
                          ]
                        }}
                        options={{
                          responsive: true,
                          scales: {
                            y: {
                              beginAtZero: true,
                              max: 100,
                              title: {
                                display: true,
                                text: 'Conversion Rate (%)'
                              }
                            }
                          },
                          plugins: {
                            legend: {
                              position: 'top'
                            },
                            title: {
                              display: true,
                              text: 'Monthly Conversion Rate'
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ManagementDashboard; 